import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
  Dropdown,
  Menu,
  Progress,
  Avatar,
  Badge,
  Tabs,
  List,
  Drawer,
  Timeline,
  Rate,
  InputNumber,
  Checkbox,
  Upload,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  FilterOutlined,
  ExportOutlined,
  ImportOutlined,
  CalendarOutlined,
  MessageOutlined,
  TrophyOutlined,
  StarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  UploadOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  FacebookOutlined,
  SearchOutlined,
  SyncOutlined,
  BellOutlined,
  TeamOutlined,
  DollarOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  source: string;
  status: 'new' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'converted' | 'lost';
  priority: 'low' | 'medium' | 'high';
  estimatedValue: number;
  assignedTo: string;
  createdDate: string;
  lastActivity: string;
  nextFollowUp: string;
  description: string;
  industry: string;
  leadScore: number;
  territory: string;
  budget: number;
  authority: boolean;
  need: string;
  timeline: string;
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  activities: Activity[];
}

interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'note' | 'task';
  title: string;
  description: string;
  date: string;
  status: 'planned' | 'completed' | 'cancelled';
  createdBy: string;
}

const LeadManagement: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    status: 'all',
    source: 'all',
    assignedTo: 'all',
    priority: 'all'
  });
  const [form] = Form.useForm();

  // Mock data - in production, this would come from API
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockLeads: Lead[] = [
        {
          id: '1',
          firstName: 'John',
          lastName: 'Smith',
          email: 'john.smith@techcorp.com',
          phone: '+1-555-0123',
          company: 'TechCorp Solutions',
          title: 'CTO',
          source: 'Website',
          status: 'qualified',
          priority: 'high',
          estimatedValue: 75000,
          assignedTo: 'Sarah Johnson',
          createdDate: '2024-06-20',
          lastActivity: '2024-06-24',
          nextFollowUp: '2024-06-26',
          description: 'Interested in enterprise software solution for their growing team.',
          industry: 'Technology',
          leadScore: 85,
          territory: 'North America',
          budget: 100000,
          authority: true,
          need: 'Enterprise software solution',
          timeline: 'Q3 2024',
          socialMedia: {
            linkedin: 'https://linkedin.com/in/johnsmith',
            twitter: '@johnsmith_tech'
          },
          activities: [
            {
              id: 'a1',
              type: 'call',
              title: 'Initial Discovery Call',
              description: 'Discussed requirements and budget',
              date: '2024-06-24T10:00:00',
              status: 'completed',
              createdBy: 'Sarah Johnson'
            },
            {
              id: 'a2',
              type: 'email',
              title: 'Follow-up Email',
              description: 'Sent product demo materials',
              date: '2024-06-24T14:30:00',
              status: 'completed',
              createdBy: 'Sarah Johnson'
            }
          ]
        },
        {
          id: '2',
          firstName: 'Emily',
          lastName: 'Davis',
          email: 'emily.davis@startup.io',
          phone: '+1-555-0124',
          company: 'Startup Innovations',
          title: 'Founder',
          source: 'LinkedIn',
          status: 'new',
          priority: 'medium',
          estimatedValue: 25000,
          assignedTo: 'Mike Chen',
          createdDate: '2024-06-23',
          lastActivity: '2024-06-23',
          nextFollowUp: '2024-06-25',
          description: 'Looking for cost-effective CRM solution for startup.',
          industry: 'Startup',
          leadScore: 65,
          territory: 'West Coast',
          budget: 30000,
          authority: true,
          need: 'CRM solution',
          timeline: 'Immediate',
          activities: []
        },
        {
          id: '3',
          firstName: 'Michael',
          lastName: 'Rodriguez',
          email: 'michael@enterprise.com',
          phone: '+1-555-0125',
          company: 'Enterprise Corp',
          title: 'VP Sales',
          source: 'Trade Show',
          status: 'proposal',
          priority: 'high',
          estimatedValue: 150000,
          assignedTo: 'Alex Thompson',
          createdDate: '2024-06-18',
          lastActivity: '2024-06-24',
          nextFollowUp: '2024-06-27',
          description: 'Large enterprise looking to upgrade their entire sales infrastructure.',
          industry: 'Manufacturing',
          leadScore: 92,
          territory: 'East Coast',
          budget: 200000,
          authority: true,
          need: 'Sales infrastructure upgrade',
          timeline: 'Q4 2024',
          activities: []
        }
      ];
      setLeads(mockLeads);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    const colors = {
      new: 'blue',
      contacted: 'cyan',
      qualified: 'green',
      proposal: 'orange',
      negotiation: 'purple',
      converted: 'success',
      lost: 'error'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red'
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  const getLeadScore = (score: number) => {
    if (score >= 80) return { color: 'success', label: 'Hot' };
    if (score >= 60) return { color: 'warning', label: 'Warm' };
    return { color: 'default', label: 'Cold' };
  };

  const handleAddLead = () => {
    setSelectedLead(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    form.setFieldsValue({
      ...lead,
      createdDate: dayjs(lead.createdDate),
      nextFollowUp: dayjs(lead.nextFollowUp)
    });
    setModalVisible(true);
  };

  const handleViewDetails = (lead: Lead) => {
    setSelectedLead(lead);
    setDetailsDrawerVisible(true);
  };

  const handleDeleteLead = (leadId: string) => {
    Modal.confirm({
      title: 'Delete Lead',
      content: 'Are you sure you want to delete this lead?',
      onOk: () => {
        setLeads(leads.filter(lead => lead.id !== leadId));
        message.success('Lead deleted successfully');
      }
    });
  };

  const handleSaveLead = async (values: any) => {
    try {
      const leadData = {
        ...values,
        id: selectedLead?.id || Date.now().toString(),
        createdDate: values.createdDate.format('YYYY-MM-DD'),
        nextFollowUp: values.nextFollowUp.format('YYYY-MM-DD'),
        lastActivity: dayjs().format('YYYY-MM-DD'),
        activities: selectedLead?.activities || []
      };

      if (selectedLead) {
        setLeads(leads.map(lead => lead.id === selectedLead.id ? leadData : lead));
        message.success('Lead updated successfully');
      } else {
        setLeads([...leads, leadData]);
        message.success('Lead created successfully');
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to save lead');
    }
  };

  const handleConvertLead = (leadId: string) => {
    Modal.confirm({
      title: 'Convert Lead',
      content: 'This will convert the lead to a customer. Continue?',
      onOk: () => {
        setLeads(leads.map(lead => 
          lead.id === leadId 
            ? { ...lead, status: 'converted' as const }
            : lead
        ));
        message.success('Lead converted to customer successfully');
      }
    });
  };

  const columns: ColumnsType<Lead> = [
    {
      title: 'Lead',
      key: 'lead',
      width: 200,
      render: (_, record) => (
        <div>
          <div>
            <Text strong>{record.firstName} {record.lastName}</Text>
            <div style={{ marginTop: 4 }}>
              <Badge 
                color={getLeadScore(record.leadScore).color} 
                text={getLeadScore(record.leadScore).label}
              />
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.title} at {record.company}
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 180,
      render: (_, record) => (
        <div>
          <div><MailOutlined /> {record.email}</div>
          <div><PhoneOutlined /> {record.phone}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {priority.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Est. Value',
      dataIndex: 'estimatedValue',
      key: 'estimatedValue',
      width: 120,
      render: (value) => `$${value.toLocaleString()}`,
      sorter: (a, b) => a.estimatedValue - b.estimatedValue,
    },
    {
      title: 'Lead Score',
      dataIndex: 'leadScore',
      key: 'leadScore',
      width: 120,
      render: (score) => (
        <div>
          <Progress 
            percent={score} 
            size="small" 
            status={score >= 80 ? 'success' : score >= 60 ? 'normal' : 'exception'}
          />
          <Text style={{ fontSize: '12px' }}>{score}/100</Text>
        </div>
      ),
      sorter: (a, b) => a.leadScore - b.leadScore,
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      width: 130,
      render: (assignedTo) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          {assignedTo}
        </Space>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source) => {
        const getSourceIcon = (src: string) => {
          switch (src.toLowerCase()) {
            case 'linkedin': return <LinkedinOutlined />;
            case 'twitter': return <TwitterOutlined />;
            case 'facebook': return <FacebookOutlined />;
            case 'website': return <GlobalOutlined />;
            default: return null;
          }
        };
        return (
          <Space>
            {getSourceIcon(source)}
            {source}
          </Space>
        );
      },
    },
    {
      title: 'Next Follow-up',
      dataIndex: 'nextFollowUp',
      key: 'nextFollowUp',
      width: 130,
      render: (date) => {
        const isOverdue = dayjs(date).isBefore(dayjs(), 'day');
        return (
          <Text style={{ color: isOverdue ? '#ff4d4f' : 'inherit' }}>
            <CalendarOutlined /> {dayjs(date).format('MMM DD')}
          </Text>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleEditLead(record)}
            />
          </Tooltip>
          <Tooltip title="Call">
            <Button type="link" icon={<PhoneOutlined />} />
          </Tooltip>
          <Tooltip title="Email">
            <Button type="link" icon={<MailOutlined />} />
          </Tooltip>
          {record.status === 'qualified' && (
            <Tooltip title="Convert to Customer">
              <Button 
                type="link" 
                icon={<CheckCircleOutlined />}
                style={{ color: '#52c41a' }}
                onClick={() => handleConvertLead(record.id)}
              />
            </Tooltip>
          )}
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item 
                  key="convert" 
                  onClick={() => handleConvertLead(record.id)}
                  disabled={record.status === 'converted'}
                >
                  Convert to Customer
                </Menu.Item>
                <Menu.Item key="duplicate">Duplicate</Menu.Item>
                <Menu.Divider />
                <Menu.Item 
                  key="delete" 
                  danger
                  onClick={() => handleDeleteLead(record.id)}
                >
                  Delete
                </Menu.Item>
              </Menu>
            }
          >
            <Button type="link">•••</Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  const filteredLeads = leads.filter(lead => {
    if (activeTab === 'hot' && lead.leadScore < 80) return false;
    if (activeTab === 'warm' && (lead.leadScore < 60 || lead.leadScore >= 80)) return false;
    if (activeTab === 'cold' && lead.leadScore >= 60) return false;
    if (activeTab === 'overdue') {
      const isOverdue = dayjs(lead.nextFollowUp).isBefore(dayjs(), 'day');
      if (!isOverdue) return false;
    }
    return true;
  });

  const leadStats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    converted: leads.filter(l => l.status === 'converted').length,
    hot: leads.filter(l => l.leadScore >= 80).length,
    totalValue: leads.reduce((sum, lead) => sum + lead.estimatedValue, 0)
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <TrophyOutlined style={{ marginRight: 8 }} />
            Lead Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<ImportOutlined />}>Import Leads</Button>
            <Button icon={<ExportOutlined />}>Export</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddLead}>
              Add Lead
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Leads"
              value={leadStats.total}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="New Leads"
              value={leadStats.new}
              prefix={<PlusOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Qualified"
              value={leadStats.qualified}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Converted"
              value={leadStats.converted}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Hot Leads"
              value={leadStats.hot}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#fa541c' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Pipeline Value"
              value={leadStats.totalValue}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="Search leads..."
              enterButton
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Option value="all">All Status</Option>
              <Option value="new">New</Option>
              <Option value="contacted">Contacted</Option>
              <Option value="qualified">Qualified</Option>
              <Option value="proposal">Proposal</Option>
              <Option value="negotiation">Negotiation</Option>
              <Option value="converted">Converted</Option>
              <Option value="lost">Lost</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="Source"
              style={{ width: '100%' }}
              value={filters.source}
              onChange={(value) => setFilters({ ...filters, source: value })}
            >
              <Option value="all">All Sources</Option>
              <Option value="website">Website</Option>
              <Option value="linkedin">LinkedIn</Option>
              <Option value="trade_show">Trade Show</Option>
              <Option value="referral">Referral</Option>
              <Option value="cold_call">Cold Call</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Lead Categories Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }}>
        <TabPane tab={`All Leads (${leads.length})`} key="all" />
        <TabPane tab={`Hot Leads (${leads.filter(l => l.leadScore >= 80).length})`} key="hot" />
        <TabPane tab={`Warm Leads (${leads.filter(l => l.leadScore >= 60 && l.leadScore < 80).length})`} key="warm" />
        <TabPane tab={`Cold Leads (${leads.filter(l => l.leadScore < 60).length})`} key="cold" />
        <TabPane 
          tab={
            <Badge 
              count={leads.filter(l => dayjs(l.nextFollowUp).isBefore(dayjs(), 'day')).length}
              offset={[10, 0]}
            >
              Overdue
            </Badge>
          } 
          key="overdue" 
        />
      </Tabs>

      {/* Leads Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredLeads}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} leads`,
          }}
          rowSelection={{
            type: 'checkbox',
            onChange: (selectedRowKeys, selectedRows) => {
              console.log('Selected leads:', selectedRows);
            },
          }}
        />
      </Card>

      {/* Add/Edit Lead Modal */}
      <Modal
        title={selectedLead ? 'Edit Lead' : 'Add New Lead'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveLead}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter first name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter last name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="company"
                label="Company"
                rules={[{ required: true, message: 'Please enter company' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title" label="Job Title">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="source"
                label="Lead Source"
                rules={[{ required: true, message: 'Please select source' }]}
              >
                <Select>
                  <Option value="Website">Website</Option>
                  <Option value="LinkedIn">LinkedIn</Option>
                  <Option value="Trade Show">Trade Show</Option>
                  <Option value="Referral">Referral</Option>
                  <Option value="Cold Call">Cold Call</Option>
                  <Option value="Social Media">Social Media</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="new">New</Option>
                  <Option value="contacted">Contacted</Option>
                  <Option value="qualified">Qualified</Option>
                  <Option value="proposal">Proposal</Option>
                  <Option value="negotiation">Negotiation</Option>
                  <Option value="converted">Converted</Option>
                  <Option value="lost">Lost</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select priority' }]}
              >
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="estimatedValue" label="Estimated Value ($)">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="budget" label="Budget ($)">
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="leadScore" label="Lead Score (0-100)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="assignedTo" label="Assigned To">
                <Select>
                  <Option value="Sarah Johnson">Sarah Johnson</Option>
                  <Option value="Mike Chen">Mike Chen</Option>
                  <Option value="Alex Thompson">Alex Thompson</Option>
                  <Option value="Lisa Wang">Lisa Wang</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nextFollowUp" label="Next Follow-up">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Additional notes about the lead..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedLead ? 'Update Lead' : 'Create Lead'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Lead Details Drawer */}
      <Drawer
        title={selectedLead ? `${selectedLead.firstName} ${selectedLead.lastName}` : 'Lead Details'}
        width={600}
        open={detailsDrawerVisible}
        onClose={() => setDetailsDrawerVisible(false)}
      >
        {selectedLead && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card size="small" title="Lead Score">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={selectedLead.leadScore}
                      status={selectedLead.leadScore >= 80 ? 'success' : 'normal'}
                    />
                    <div style={{ marginTop: 8 }}>
                      <Badge 
                        color={getLeadScore(selectedLead.leadScore).color}
                        text={getLeadScore(selectedLead.leadScore).label}
                      />
                    </div>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="BANT Qualification">
                  <div>
                    <div>Budget: ${selectedLead.budget?.toLocaleString()}</div>
                    <div>Authority: {selectedLead.authority ? '✓ Yes' : '✗ No'}</div>
                    <div>Need: {selectedLead.need}</div>
                    <div>Timeline: {selectedLead.timeline}</div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">Contact Information</Divider>
            <div style={{ marginBottom: 16 }}>
              <p><strong>Email:</strong> {selectedLead.email}</p>
              <p><strong>Phone:</strong> {selectedLead.phone}</p>
              <p><strong>Company:</strong> {selectedLead.company}</p>
              <p><strong>Title:</strong> {selectedLead.title}</p>
              <p><strong>Industry:</strong> {selectedLead.industry}</p>
            </div>

            <Divider orientation="left">Lead Details</Divider>
            <div style={{ marginBottom: 16 }}>
              <p><strong>Source:</strong> {selectedLead.source}</p>
              <p><strong>Status:</strong> 
                <Tag color={getStatusColor(selectedLead.status)} style={{ marginLeft: 8 }}>
                  {selectedLead.status.toUpperCase()}
                </Tag>
              </p>
              <p><strong>Priority:</strong>
                <Tag color={getPriorityColor(selectedLead.priority)} style={{ marginLeft: 8 }}>
                  {selectedLead.priority.toUpperCase()}
                </Tag>
              </p>
              <p><strong>Estimated Value:</strong> ${selectedLead.estimatedValue.toLocaleString()}</p>
              <p><strong>Assigned To:</strong> {selectedLead.assignedTo}</p>
              <p><strong>Next Follow-up:</strong> {dayjs(selectedLead.nextFollowUp).format('MMMM DD, YYYY')}</p>
            </div>

            <Divider orientation="left">Description</Divider>
            <p>{selectedLead.description}</p>

            <Divider orientation="left">Activity Timeline</Divider>
            <Timeline>
              {selectedLead.activities.map((activity) => (
                <Timeline.Item
                  key={activity.id}
                  color={activity.status === 'completed' ? 'green' : 'blue'}
                >
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{activity.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {activity.type.toUpperCase()} • {dayjs(activity.date).format('MMM DD, YYYY HH:mm')} • {activity.createdBy}
                    </div>
                    <div style={{ marginTop: 4 }}>{activity.description}</div>
                  </div>
                </Timeline.Item>
              ))}
              <Timeline.Item color="gray">
                <div style={{ color: '#666' }}>Lead created on {dayjs(selectedLead.createdDate).format('MMMM DD, YYYY')}</div>
              </Timeline.Item>
            </Timeline>

            <div style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" onClick={() => handleEditLead(selectedLead)}>
                  Edit Lead
                </Button>
                <Button icon={<PhoneOutlined />}>Call</Button>
                <Button icon={<MailOutlined />}>Email</Button>
                <Button icon={<CalendarOutlined />}>Schedule Meeting</Button>
                {selectedLead.status === 'qualified' && (
                  <Button 
                    type="primary" 
                    style={{ backgroundColor: '#52c41a' }}
                    onClick={() => handleConvertLead(selectedLead.id)}
                  >
                    Convert to Customer
                  </Button>
                )}
              </Space>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LeadManagement;
